import { Component, Element, h, Host, Prop, Watch, JSX, Fragment, State } from '@stencil/core';
import { debounce } from 'debounce';
import { UiCmsFootnoteListBody } from '../ui-cms-footnote-list/ui-cms-footnote-list';
import { CmsImage, CmsLink, CmsMovingImage, CmsVideo } from '../../types/global';
import { replaceCheckmarkIcons } from '../../global/utility/utils';
import { renderLink } from '../../global/utility/link-renderer';

interface CmsImageWithLightbox extends CmsImage {
  lightbox?: boolean;
}

interface UiCmsTextMediaBody {
  image?: CmsImageWithLightbox;
  video?: CmsVideo;
  movingImage?: CmsMovingImage;
  linkList?: CmsLink[];
  footnotes?: UiCmsFootnoteListBody;
  richtext?: string;

  // layout properties
  order: 'media-first' | 'text-first'; // does not apply to mobile
  richtextColumns?: 1 | 2;
  mediaSize?: 'xsmall' | 'small' | 'medium' | 'large' | 'full';
  verticalAlign?: boolean;
}

@Component({
  tag: 'ui-cms-text-media',
  styleUrl: 'ui-cms-text-media.scss',
})
export class UiCmsTextMedia {
  @Element() el: HTMLUiCmsTextMediaElement;

  @Prop({ mutable: true }) body: UiCmsTextMediaBody = {} as any;

  @Prop({ attribute: 'body' }) utilityBody: string;

  @Watch('utilityBody')
  utilityBodyChangeHandler(newValue: string) {
    this.body = newValue ? JSON.parse(newValue) : this.body;
  }

  @Prop({ mutable: true }) i18n = {
    enlargeImage: 'Bild vergrößern',
    startAnimation: 'Animation starten',
    stopAnimation: 'Animation stoppen',
  };

  @Prop({ attribute: 'i18n' }) utilityI18n: string;

  @Watch('utilityI18n')
  utilityI18nChangeHandler(newValue: string) {
    if (newValue) this.i18n = { ...this.i18n, ...JSON.parse(newValue) };
  }

  @Prop() inverted: boolean;

  @State() order: 'media-first' | 'text-first' = 'media-first';

  @State() isMovingImagePlaying: boolean = true;

  private movingImageElement: HTMLVideoElement;

  private refDialog: HTMLDialogElement;

  private get hasMediaContent(): boolean {
    return !!(this.body?.image?.src || this.body?.video?.videoId || this.body?.movingImage?.src);
  }

  private get hasTextContent(): boolean {
    return !!(this.body?.richtext || this.body?.footnotes || this.body?.linkList);
  }

  private calculateButtonPosition(): void {
    // In some cases the moving-image-container is bigger then the moving image
    if (!this.body.movingImage?.src) return; // Do not run this if there is no moving image

    const movingImageContainerElement = this.el.querySelector<HTMLDivElement>('.moving-image-container');
    if (!movingImageContainerElement) return;

    const movingImageElement = movingImageContainerElement.querySelector('video');
    if (!movingImageElement) return;

    const containerHeight = movingImageContainerElement.clientHeight;
    const imageHeight = movingImageElement.clientHeight;

    movingImageContainerElement.style.setProperty('--moving-image-offset', `${containerHeight - imageHeight}px`);
  }

  private resizeObserver: ResizeObserver = new ResizeObserver(
    debounce((e) => {
      // this step is necessary because on small width the media is always first
      const { width } = e[0].contentRect;
      this.order = width <= 480 ? 'media-first' : this.body.order;
      this.calculateButtonPosition();
    }, 200)
  );

  private openLightbox(): void {
    this.refDialog.show();
  }

  componentWillLoad() {
    this.utilityBodyChangeHandler(this.utilityBody);
    this.utilityI18nChangeHandler(this.utilityI18n);
    this.order = this.body.order;
  }

  componentDidLoad() {
    replaceCheckmarkIcons(this.el);
    this.resizeObserver.observe(this.el);
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  private renderMedia(): JSX.Element {
    return (
      this.hasMediaContent && (
        <div class={{ media: true, 'media-moving-image': !!this.body?.movingImage?.src }}>
          {this.body?.image?.src && this.renderImage()}
          {this.body?.movingImage?.src && this.renderMovingImage()}
          {this.body?.video?.videoId && this.renderVideo()}
        </div>
      )
    );
  }

  private renderText(): JSX.Element {
    return (
      this.hasTextContent && (
        <div class="richtext">
          {this.body.richtext && (
            <div
              class={{
                'sd-prose sd-prose--full-width columns': true,
                'sd-prose--inverted': this.inverted,
                [`richtext-columns--${this.body.richtextColumns}`]: true,
              }}
              innerHTML={this.body.richtext}
            ></div>
          )}
          {this.body.footnotes && (
            <ui-cms-footnote-list inverted={this.inverted} body={this.body.footnotes}></ui-cms-footnote-list>
          )}
          {this.body.linkList && this.body.linkList.length === 1 && renderLink(this.body.linkList[0], this.inverted)}
          {this.body.linkList && this.body.linkList.length > 1 && (
            <ui-cms-link-list
              inverted={this.inverted}
              body={{ links: this.body.linkList, columns: 1 }}
            ></ui-cms-link-list>
          )}
        </div>
      )
    );
  }

  private renderImage(): JSX.Element {
    const { lightbox, copyrightDetails, caption } = this.body.image;

    const copyrightStyle = [
      'sd-copyright',
      copyrightDetails?.color === 'black' ? 'sd-copyright--color-black' : '',
      copyrightDetails?.placement === 'top' ? 'sd-copyright--placement-top' : '',
    ].join(' ');

    return (
      <figure class={{ 'sd-media image': true, 'image--lightbox': lightbox, 'sd-media--inverted': this.inverted }}>
        {copyrightDetails?.text ? (
          <div
            class={{ [copyrightStyle]: true }}
            style={{
              '--copyright': `'© ${copyrightDetails.text}'`,
            }}
          >
            {this.renderCmsImage()}
          </div>
        ) : (
          this.renderCmsImage()
        )}
        {caption && <figcaption innerHTML={caption}></figcaption>}
        {lightbox && this.renderKeyboardLightboxTrigger()}
      </figure>
    );
  }

  private renderCmsImage(): JSX.Element {
    const { src, alt, mobileSrc, lightbox } = this.body.image;
    return (
      <ui-cms-image
        src={src}
        alt={alt}
        size={2400}
        mobileSrc={mobileSrc}
        onClick={() => {
          if (lightbox) this.openLightbox();
        }}
      ></ui-cms-image>
    );
  }

  private toggleMovingImage(): void {
    if (this.movingImageElement) {
      if (!this.isMovingImagePlaying) {
        this.movingImageElement.play();
        this.isMovingImagePlaying = true;
      } else {
        this.movingImageElement.pause();
        this.isMovingImagePlaying = false;
      }
    }
  }

  private renderMovingImage(): JSX.Element {
    const { masked, src } = this.body.movingImage;
    return (
      <div class="moving-image-container">
        <video
          class={{ 'moving-image': true, 'moving-image--masked': masked }}
          loop
          playsInline
          autoPlay
          muted
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          disablePictureInPicture
          ref={(el: HTMLVideoElement) => {
            this.movingImageElement = el;
          }}
        >
          <source src={src.replace('mp4', 'webm')} type="video/webm" />
          <source src={src} type="video/mp4" />
        </video>
        <sd-button inverted="true" class="moving-image__toggle" onClick={() => this.toggleMovingImage()}>
          <span class="sr-only">{this.isMovingImagePlaying ? this.i18n.stopAnimation : this.i18n.startAnimation}</span>
          <sd-icon
            library={window.ui.cmsState.iconLibrary}
            name={this.isMovingImagePlaying ? 'union-investment/system/pause' : 'union-investment/system/play'}
          />
        </sd-button>
      </div>
    );
  }

  private renderVideo(): JSX.Element {
    const { videoId, loop, muted, copyrightDetails } = this.body.video;

    const copyrightStyle = [
      'sd-copyright',
      copyrightDetails?.color === 'black' ? 'sd-copyright--color-black' : '',
      copyrightDetails?.placement === 'top' ? 'sd-copyright--placement-top' : '',
    ].join(' ');

    return (
      <figure class={{ video: true, 'sd-media--inverted': this.inverted }}>
        {copyrightDetails?.text ? (
          <div
            class={{ [copyrightStyle]: true }}
            style={{
              '--copyright': `'© ${copyrightDetails.text}'`,
            }}
          >
            <ui-cms-video videoId={videoId} loop={loop} muted={muted}></ui-cms-video>
          </div>
        ) : (
          <ui-cms-video videoId={videoId} loop={loop} muted={muted}></ui-cms-video>
        )}
      </figure>
    );
  }

  private renderLightbox(): JSX.Element {
    return (
      <sd-dialog
        ref={(el) => {
          this.refDialog = el;
        }}
      >
        {this.renderCmsImage()}
      </sd-dialog>
    );
  }

  private renderKeyboardLightboxTrigger(): JSX.Element {
    return (
      <sd-button
        variant="primary"
        size="md"
        onClick={() => {
          this.openLightbox();
        }}
      >
        <sd-icon
          label={this.i18n.enlargeImage}
          library={window.ui.cmsState.iconLibrary}
          name="union-investment/system/enlarge"
        ></sd-icon>
      </sd-button>
    );
  }

  render() {
    return (
      <Host>
        <div class="text-media">
          <div
            class={{
              content: true,
              [`content-media--${this.body.mediaSize}`]: this.hasMediaContent,
              [`content--${this.order}`]: true,
              'content--vertical-align': this.body.verticalAlign,
            }}
          >
            {this.order === 'text-first' ? (
              <Fragment>
                {this.renderText()}
                {this.renderMedia()}
              </Fragment>
            ) : (
              <Fragment>
                {this.renderMedia()}
                {this.renderText()}
              </Fragment>
            )}
          </div>
        </div>
        {this.body?.image?.lightbox && this.renderLightbox()}
      </Host>
    );
  }
}
